import ChipButton from 'app_components/commons/ChipButton/ChipButton'
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import TutorialsCard from 'app_components/screens/landing/tutorials/components/TutorialsCard/TutorialsCard'
import LandingService from 'app_services/admin/landings/landingService'
import { ILanding, ILandingTutorial, IParamsGetLandingData } from 'app_types/admin/landings/landingTypes'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { MdDone } from 'react-icons/md'
import TableItemsPaginator, { ItemsPaginationAlign } from '../TableItemsPaginator/TableItemsPaginator'

import './TutorialsModule.scss'
import { IRoleListItem } from '../../../types/admin/role/roleTypes';
import { ITerm } from 'app_types/admin/term/term.types'

interface ITutorialsModuleProps {
  privateLanding?: boolean
}

interface IGroup {
  label: string
  value: string
}

const ITEMS_PER_PAGE = 12
const LANDING_SLUG = 'tutorials'

const TutorialsModule: FC<ITutorialsModuleProps> = ({ privateLanding }) => {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const defaultValue = urlSearchParams.get('defaultValue')
  const [landing, updateLanding] = useState<ILanding | undefined>(undefined)
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const tutorials = useMemo(() => {
    if (landing?.landing?.tutorials?.length) {
      return landing.landing.tutorials.sort((a, b) => Number(a.position) - Number(b.position))
    }
    return []
  }, [landing])

  const landingService = useMemo(() => new LandingService(), [])

  const getActiveGroups = (): IGroup[] => {
    if (tutorials?.length && !privateLanding) {
      let allGroups: ITerm[] = []
      tutorials.forEach((tutorial) => {
        tutorial?.publicGroups
          ?.forEach((publicGroup) => {
            if (!allGroups.some(group => group._id === publicGroup?._id)) {
              allGroups.push(publicGroup)
            }
          })
      })
      return allGroups
        ?.sort((a, b) => Number(a.position) - Number(b.position))
        ?.map((term) => ({ label: term.name, value: term._id }))
    }
    if (tutorials?.length && privateLanding) {
      let allGroups: ITerm[] = []
      tutorials
        .filter((tutorial) =>
          tutorial?.privateGroups?.some((privateGroup) => (privateGroup.role as IRoleListItem)?._id === defaultValue),
        )
        .forEach((tutorial) => {
          const privateGroup = tutorial?.privateGroups?.find((pg) => (pg.role as IRoleListItem)?._id === defaultValue)
          privateGroup?.groups?.forEach((groupItem) => {
            if (!allGroups.some(group => group._id === groupItem?._id)) {
              allGroups.push(groupItem)
            }
          })
        })
      return allGroups
        ?.sort((a, b) => Number(a.position) - Number(b.position))
        ?.map((term) => ({ label: term.name, value: term._id }))
    }
    return []
  }

  const getLandingData = useCallback(async () => {
    let httpParams: IParamsGetLandingData = {
      slug: LANDING_SLUG,
      onlyActiveTutorials: true,
      onlyPublicTutorials: privateLanding ? false : true,
    }
    const landingResponse = await landingService.fetchLandingData(httpParams)
    if (landingResponse) {
      updateLanding(landingResponse)
    }
  }, [landingService, privateLanding])

  const handleClickGroup = (group: IGroup | null) => {
    setActiveGroup(group?.value as string)
  }

  const tutorialsToRender = privateLanding
    ? tutorials
      ?.filter((tutorial) =>
        tutorial?.privateGroups?.some((privateGroup) => (privateGroup.role as IRoleListItem)?._id === defaultValue),
      )?.filter((tutorial) => {
        const privateGroup = tutorial?.privateGroups?.find((pg) => (pg.role as IRoleListItem)?._id === defaultValue)
        return !activeGroup || privateGroup?.groups?.some((group) => group?._id === activeGroup)
      })
    : tutorials?.filter((tutorial) => !activeGroup || tutorial?.publicGroups?.some((group) => group?._id === activeGroup))

  useEffect(() => {
    getLandingData()
  },[getLandingData])

  return (
    <div className="tutorials-module--layout">
      <TextComponent type="subtitle">
        Tutoriales
      </TextComponent>
      <div className='tutorials-module--chips-list'>
        {getActiveGroups()?.map((group) => (
          <ChipButton
            active={activeGroup === group?.value}
            activeColor='#F3DAFF'
            activeIcon={<MdDone color="#2E004D" />}
            onClick={() => handleClickGroup(group)}
            key={group?.value}
          >
            {group?.label}
          </ChipButton>
        ))}
        <ChipButton
          active={!activeGroup}
          activeColor='#F3DAFF'
          activeIcon={<MdDone color="#2E004D" />}
          onClick={() => handleClickGroup(null)}
        >
          Todos
        </ChipButton>
      </div>
      <TableItemsPaginator
        alignPagination={ItemsPaginationAlign.CENTER}
        items={tutorialsToRender}
        perPage={ITEMS_PER_PAGE}
      >
        {(itemsToRender) => (
          <div className="tutorials-module--list">
            {itemsToRender.map((tutorial: ILandingTutorial, idx: number) => (
              <TutorialsCard key={tutorial.title + idx} tutorial={tutorial} />
            ))}
          </div>
        )}
      </TableItemsPaginator>
    </div>
  )
}

export default TutorialsModule
